import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Button, Card, Spinner, Alert } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronLeft, FaChevronRight, FaTimes } from 'react-icons/fa';
import IconWrapper from './IconWrapper';
import { useGallery } from '../hooks/useGallery';
import './Gallery.css';

type LightboxItem = {
  src: string;
  title: string;
  alt: string;
  category: string;
};

const Gallery: React.FC = () => {
  const [lightboxItems, setLightboxItems] = useState<LightboxItem[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const {
    images,
    categories,
    loading,
    error,
    getImageUrl,
    getPlaceholderUrl,
  } = useGallery();

  /** otevře lightbox jen s fotkami stejné kategorie jako kliknutá karta */
  const openLightbox = (clickedId: number) => {
    const clicked = images.find(i => i.id === clickedId);
    if (!clicked) return;

    // 1) vytvoř playlist jen pro danou kategorii
    const sameCategory = images.filter(i => i.category === clicked.category);

    // 2) z každé položky udělej 1..n snímků (podle toho, zda je filename string nebo string[])
    const list: LightboxItem[] = [];
    sameCategory.forEach(img => {
      const fn = img.filename;
      if (Array.isArray(fn)) {
        fn.forEach(f =>
          list.push({
            src: getImageUrl(f),
            title: img.title,
            alt: img.alt,
            category: img.category,
          })
        );
      } else {
        list.push({
          src: getImageUrl(fn),
          title: img.title,
          alt: img.alt,
          category: img.category,
        });
      }
    });

    // 3) najdi výchozí index — pokud kliknutá karta má pole, bereme její první fotku
    let startSrc = Array.isArray(clicked.filename) ? getImageUrl(clicked.filename[0]) : getImageUrl(clicked.filename);
    const startIndex = list.findIndex(it => it.src === startSrc);

    setLightboxItems(list);
    setLightboxIndex(startIndex >= 0 ? startIndex : 0);
  };

  const closeLightbox = () => {
    setLightboxIndex(null);
    setLightboxItems([]);
  };

  const goNext = () => {
    if (lightboxIndex == null || lightboxItems.length === 0) return;
    setLightboxIndex((lightboxIndex + 1) % lightboxItems.length);
  };

  const goPrev = () => {
    if (lightboxIndex == null || lightboxItems.length === 0) return;
    setLightboxIndex((lightboxIndex - 1 + lightboxItems.length) % lightboxItems.length);
  };

  // klávesy (Esc, ←, →)
  useEffect(() => {
    if (lightboxIndex == null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxIndex, lightboxItems.length]);

  if (loading) {
    return (
      <div className="gallery-page">
        <div className="gallery-hero">
          <Container>
            <div className="text-center">
              <h1 className="gallery-title">Portfolio</h1>
              <p className="gallery-subtitle">Prohlédněte si ukázku mé práce</p>
            </div>
          </Container>
        </div>
        <Container className="py-5">
          <div className="text-center">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Načítám galerii...</p>
          </div>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="gallery-page">
        <div className="gallery-hero">
          <Container>
            <div className="text-center">
              <h1 className="gallery-title">Portfolio</h1>
              <p className="gallery-subtitle">Prohlédněte si ukázku mé práce</p>
            </div>
          </Container>
        </div>
        <Container className="py-5">
          <Alert variant="danger">
            <Alert.Heading>Chyba pri načítaní galérie</Alert.Heading>
            <p>{error}</p>
          </Alert>
        </Container>
      </div>
    );
  }

  return (
    <div className="gallery-page">
      <div className="gallery-hero">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="gallery-title">Galerie</h1>
            <p className="gallery-subtitle">Prohlédněte si ukázku mé práce.</p>
          </motion.div>
        </Container>
      </div>

      <Container className="py-5">
        <Row>
          <AnimatePresence mode="wait">
            {images.map((image, index) => {
              const thumbFile = Array.isArray(image.filename) ? image.filename[0] : image.filename;
              const thumbUrl = getImageUrl(thumbFile);

              return (
                <Col lg={4} md={6} sm={6} key={image.id}>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    layout
                  >
                    <Card className="gallery-card" onClick={() => openLightbox(image.id)}>
                      <div
                        className="image-container"
                        role="button"
                        aria-label="Zobraziť obrázok"
                      >
                        <img
                          src={thumbUrl}
                          alt={image.alt}
                          className="gallery-image"
                          onError={(e) => {
                            const target = e.currentTarget as HTMLImageElement;
                            target.src = getPlaceholderUrl();
                          }}
                        />
                        <div className="image-overlay">
                          <Button
                            type="button"
                            variant="light"
                            size="sm"
                            className="view-btn"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              openLightbox(image.id);
                            }}
                          >
                            Zobrazit
                          </Button>
                        </div>
                      </div>

                      <Card.Body>
                        <Card.Title>{image.title}</Card.Title>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              );
            })}
          </AnimatePresence>
        </Row>

        {images.length === 0 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-5">
            <h3>Žiadne obrázky sa nenašli</h3>
            <p className="text-muted">Skúste to prosím neskôr.</p>
          </motion.div>
        )}
      </Container>

      {/* LIGHTBOX – jen v rámci jedné kategorie */}
      {lightboxIndex != null && lightboxItems.length > 0 && (
        <motion.div
          className="lightbox-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={closeLightbox}
        >
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="lightbox-close" onClick={closeLightbox} aria-label="Zavrieť">
              <IconWrapper icon={FaTimes} />
            </button>

            <button type="button" className="lightbox-arrow left" onClick={goPrev} aria-label="Predchádzajúci">
              <IconWrapper icon={FaChevronLeft} />
            </button>

            <img
              className="lightbox-image"
              src={lightboxItems[lightboxIndex].src}
              alt={lightboxItems[lightboxIndex].alt}
              onError={(e) => {
                const target = e.currentTarget as HTMLImageElement;
                target.src = getPlaceholderUrl();
              }}
            />

            <button type="button" className="lightbox-arrow right" onClick={goNext} aria-label="Nasledujúci">
              <IconWrapper icon={FaChevronRight} />
            </button>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default Gallery;